

import { useState, useEffect } from 'react';
import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { useProperties } from 'features/property/hooks/useProperties';
import { usePrefetchProperties } from 'features/property/hooks/usePrefetchProperties';
import { useUpdateProperty } from 'features/property/hooks/useUpdateProperty';
import { useURLSync } from 'features/property/hooks/useURLSync';
import { ViewPropertyModal } from 'features/property/components/ViewPropertyModal';
import { DeleteConfirmModal } from 'features/property/components/DeleteConfirmModal';
import { EditPropertyModal } from 'features/property/components/EditPropertyModal';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import { propertyServices } from 'features/property/services/property.service';
import {
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Pencil,
  Download,
  Building,
} from 'lucide-react';
import { formatDateTime } from 'utils/dateFormatter';

const PropertyManagement = () => {
  const initialFilters = {
    page: '1',
    limit: '10',
    search: '',
    propertyCategory: 'all',
    propertyType: 'all',
    approvalStatus: 'all',
    furnishing: 'all',
    constructionStatus: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  };

  const [filters, setFilters] = useURLSync(initialFilters);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  const { data, isLoading, isError, error } = useProperties(filters);

  const { data: profileData } = useUserProfile();
const user = profileData?.data;

const restrictedRoles = [
  "delivery_manager",
  "category_manager",
  "operations_manager",
  "travel_manager",
  "finance_manager",
];

const showPermissionError = restrictedRoles.includes(
  user?.role?.name
);
  const { prefetchNextPage, prefetchPrevPage } = usePrefetchProperties();
  const { mutateAsync: updateProperty } = useUpdateProperty();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters((prev) => ({ ...prev, search: searchInput, page: '1' }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (data?.pagination?.totalPages) {
      prefetchNextPage(filters, data.pagination.totalPages);
      prefetchPrevPage(filters);
    }
  }, [filters, data?.pagination?.totalPages, prefetchNextPage, prefetchPrevPage]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key !== 'page' && { page: '1' }),
    }));
  };


  const handleSort = (column) => {
  const newOrder =
    filters.sortBy === column && filters.sortOrder === "asc"
      ? "desc"
      : "asc";

  setFilters((prev) => ({
    ...prev,
    sortBy: column,
    sortOrder: newOrder,
    page: "1",
  }));
};

const SortIcon = ({ column }) => {
  if (filters.sortBy !== column) {
    return <span className="text-gray-400">↕</span>;
  }

  return filters.sortOrder === "asc"
    ? <span>↑</span>
    : <span>↓</span>;
};

  const handleStatusToggle = async (property) => {
    const newStatus = property.status === 'active' ? 'inactive' : 'active';
    setPendingId(property._id);
    try {
      await updateProperty({
        propertyId: property._id,
        data: { status: newStatus },
      });
    } finally {
      setPendingId(null);
    }
  };

  const handleEditProperty = (property) => {
    setSelectedProperty(property);
    setIsEditModalOpen(true);
  };

  const handleViewProperty = (property) => {
    setSelectedProperty(property);
    setIsViewModalOpen(true);
  };

  const handleDeleteClick = (property) => {
    setSelectedProperty(property);
    setIsDeleteModalOpen(true);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className='flex items-center justify-center min-h-screen'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
        </div>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <div className='flex items-center justify-center min-h-screen'>
          <div className='text-red-500'>
            Error: {error?.message || 'Failed to load properties'}
          </div>
        </div>
      </Layout>
    );
  }

  const properties = data?.data || [];

const handleExport = async () => {
  try {
    const response =
      await propertyServices.exportProperties(
        {
          propertyCategory:
            filters.propertyCategory,

          propertyType:
            filters.propertyType,

          approvalStatus:
            filters.approvalStatus,

          furnishing:
            filters.furnishing,

          constructionStatus:
            filters.constructionStatus,

          search:
            filters.search,

          sortBy:
            filters.sortBy,

          sortOrder:
            filters.sortOrder,
        }
      );

    const blob = new Blob(
      [response.data],
      {
        type: 'text/csv',
      }
    );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement('a');

    link.href = url;

    link.download = `properties-${Date.now()}.csv`;

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    window.URL.revokeObjectURL(
      url
    );
  } catch (error) {
    console.error(
      'Failed to export properties',
      error
    );
  }
};
  const pagination = data?.pagination || {};

  return (
    <Layout>
      <div className={`min-h-screen bg-background text-foreground p-6 ${PAGE_CONTENT_WIDTH}`}>
          {/* Header */}
          <PageHeader
            icon={Building}
            title="Property Management"
            subtitle="Manage property listings and approvals"
            actions={
              !showPermissionError && (
                <button
                  onClick={handleExport}
                  className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
                >
                  <Download size={16} />
                  <span>Export</span>
                </button>
              )
            }
          />

          {/* Filters */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-6'>
            <div className='relative lg:col-span-2'>
              <Search
                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground'
                size={16}
              />
              <input
                type='text'
                placeholder='Search properties...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className='w-full pl-10 pr-10 py-2 text-sm'
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className='absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground'
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <select
              value={filters.propertyCategory}
              onChange={(e) => updateFilter('propertyCategory', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Categories</option>
              <option value='commercial'>Commercial</option>
              <option value='residential'>Residential</option>
            </select>

            <select
              value={filters.propertyType}
              onChange={(e) => updateFilter('propertyType', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Types</option>
              <option value='flat_apartment'>Flat / Apartment</option>
              <option value='house'>House</option>
              <option value='villa'>Villa</option>
              <option value='building_floor'>Building Floor</option>
              <option value='plot'>Plot</option>
              <option value='studio_apartment'>Studio Apartment</option>
              <option value='pent_house'>Pent House</option>
            </select>

            <select
              value={filters.approvalStatus}
              onChange={(e) => updateFilter('approvalStatus', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Approvals</option>
              <option value='pending'>Pending</option>
              <option value='approved'>Approved</option>
              <option value='rejected'>Rejected</option>
            </select>

            <select
              value={filters.limit}
              onChange={(e) => updateFilter('limit', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='5'>5 per page</option>
              <option value='10'>10 per page</option>
              <option value='20'>20 per page</option>
              <option value='50'>50 per page</option>
            </select>
          </div>

          {/* Table */}
          <div className='bg-card border border-border rounded-lg overflow-hidden'>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-muted border-b border-border'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Image</th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Location</th> */}
                    <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
  onClick={() => handleSort("propertyLocation")}
>
  <div className="flex items-center space-x-1">
    <span>Location</span>
    <SortIcon column="propertyLocation" />
  </div>
</th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Category</th> */}
                    <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
  onClick={() => handleSort("propertyCategory")}
>
  <div className="flex items-center space-x-1">
    <span>Category</span>
    <SortIcon column="propertyCategory" />
  </div>
</th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Type</th> */}
                    <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
  onClick={() => handleSort("propertyType")}
>
  <div className="flex items-center space-x-1">
    <span>Type</span>
    <SortIcon column="propertyType" />
  </div>
</th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>City</th> */}
                    <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
  onClick={() => handleSort("address.city")}
>
  <div className="flex items-center space-x-1">
    <span>City</span>
    <SortIcon column="address.city" />
  </div>
</th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Seller</th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Status</th> */}
                    <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
  onClick={() => handleSort("status")}
>
  <div className="flex items-center space-x-1">
    <span>Status</span>
    <SortIcon column="status" />
  </div>
</th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Approval</th> */}
                    <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
  onClick={() => handleSort("approvalStatus")}
>
  <div className="flex items-center space-x-1">
    <span>Approval</span>
    <SortIcon column="approvalStatus" />
  </div>
</th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Created</th> */}
                    <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
  onClick={() => handleSort("createdAt")}
>
  <div className="flex items-center space-x-1">
    <span>Created</span>
    <SortIcon column="createdAt" />
  </div>
</th>
                    <th className='px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase'>Actions</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border'>
                  {showPermissionError ? (
    <tr>
      <td
        colSpan='10'
        className='px-4 py-8 text-center text-destructive'
      >
        Error loading properties: You do not have permission to perform this action!
      </td>
    </tr>
  ) : properties.length === 0 ? (
                    <tr>
                      <td colSpan='10' className='px-4 py-8 text-center text-muted-foreground'>
                        No properties found
                      </td>
                    </tr>
                  ) : (
                    properties.map((property) => (
                      <tr key={property._id} className='hover:bg-muted/50 transition-colors'>
                        <td className='px-4 py-3'>
                          <img
                            src={
                              // photos[].url in DB is the S3 key;
                              // API transforms it to preSignedUrl before sending back.
                              // So what we receive here is already a preSignedUrl.
                              property.photos?.[0]?.url || '/placeholder-property.png'
                            }
                            alt={property.propertyLocation}
                            className='w-12 h-12 rounded-lg object-cover border border-border'
                          />
                        </td>
                        <td className='px-4 py-3 text-sm text-foreground max-w-[200px] truncate'>
                          {property.propertyLocation}
                        </td>
                        <td className='px-4 py-3 text-sm'>
                          <span className='px-2 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs capitalize'>
                            {property.propertyCategory}
                          </span>
                        </td>
                        <td className='px-4 py-3 text-sm'>
                          <span className='px-2 py-1 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs whitespace-nowrap'>
                            {property.propertyType?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className='px-4 py-3 text-sm text-foreground max-w-[150px] truncate'>
                          {property.address?.city}, {property.address?.state}
                        </td>
                        <td className='px-4 py-3 text-sm text-foreground whitespace-nowrap'>
                          {property.sellerId?.firstName} {property.sellerId?.lastName}
                        </td>
                        <td className='px-4 py-3 text-sm'>
                          <button
                            disabled={pendingId === property._id}
                            onClick={() => handleStatusToggle(property)}
                            className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors
                              ${property.status === 'active'
                                ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                                : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {pendingId === property._id ? '...' : property.status}
                          </button>
                        </td>
                        <td className='px-4 py-3 text-sm'>
                          <span className={`px-2 py-1 rounded-full text-xs capitalize ${
                            property.approvalStatus === 'approved'
                              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                              : property.approvalStatus === 'rejected'
                                ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                                : 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                          }`}>
                            {property.approvalStatus}
                          </span>
                        </td>
                        <td className='px-4 py-3 text-sm text-muted-foreground'>
                          {formatDateTime(property.createdAt)}
                        </td>
                        <td className='px-4 py-3'>
                          <div className='flex items-center justify-end space-x-2'>
                            <button
                              onClick={() => handleEditProperty(property)}
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Edit Property'
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              onClick={() => handleViewProperty(property)}
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='View Details'
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(property)}
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Delete Property'
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!showPermissionError && pagination && pagination.totalPages > 1 && (
              <div className='flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border space-y-3 sm:space-y-0'>
                <div className='text-sm text-muted-foreground'>
                  Showing{' '}
                  {((pagination.page || pagination.currentPage) - 1) *
                    (pagination.limit || pagination.itemsPerPage) + 1}{' '}
                  to{' '}
                  {Math.min(
                    (pagination.page || pagination.currentPage) *
                      (pagination.limit || pagination.itemsPerPage),
                    pagination.total || pagination.totalItems,
                  )}{' '}
                  of {pagination.total || pagination.totalItems} properties
                </div>

                <div className='flex items-center space-x-2'>
                  <button
                    onClick={() =>
                      updateFilter('page', String((pagination.page || pagination.currentPage) - 1))
                    }
                    disabled={(pagination.page || pagination.currentPage) === 1}
                    className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className='flex items-center space-x-1'>
                    {[...Array(pagination.totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      const current = pagination.page || pagination.currentPage;
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= current - 1 && page <= current + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => updateFilter('page', String(page))}
                            className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                              page === current
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === current - 2 || page === current + 2) {
                        return <span key={page} className='text-muted-foreground'>...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() =>
                      updateFilter('page', String((pagination.page || pagination.currentPage) + 1))
                    }
                    disabled={(pagination.page || pagination.currentPage) >= pagination.totalPages}
                    className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
      </div>

      <ViewPropertyModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        property={selectedProperty}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        property={selectedProperty}
      />
      <EditPropertyModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        property={selectedProperty}
      />
    </Layout>
  );
};

export default PropertyManagement;